import React, { useEffect, useState } from 'react';
import {
  CARD_W,
  POLL_CARD_H, POLL_CARD_GAP, PLUS_R,
  CIRCLE_R,
  C, ny,
} from '../flowCanvas.constants';

const BORDER_R   = 8;
const BADGE_W    = 56;
const BADGE_H    = 18;
const CARD_PAD   = 10;
const EXPAND_BTN = 18; // chevron button size
const EDITOR_QUESTION_H = 52;
const EDITOR_ANSWER_ROW_H = 32;
const EDITOR_ADD_BTN_H = 26;
const EDITOR_SECTION_GAP = 10;
const EDITOR_BOTTOM_PAD = 24;
const EDITOR_CONTENT_BUFFER = 36;
const MAX_POLL_ANSWERS = 10;

const TYPE_COLORS = {
  opinion:    '#38bdf8',
  predictive: '#f59e0b',
  removal:    '#f97316',
};

// Heights for a single poll card
const COLLAPSED_H = 74;
const ANSWER_ROW_H = 22;  // height per answer row in expanded view
const EXPANDED_EXTRA = 30; // space for question text + padding above answers

/**
 * PollCardsNode — each poll is its own independent card with collapse/expand.
 * Collapsed: shows type badge + title.
 * Expanded:  adds question text + answer list.
 *
 * Props:
 *   step, stepX, centreY, isSelected, liveCustomisationData,
 *   parentStepX, parentStepY, onPollClick, onAddPollClick
 *   audioSiblingRightX, audioSiblingMidY – right edge X and centre Y of the pollAudio
 *     sibling node, so we can draw its exit line into the same collection stem.
 */
export default function PollCardsNode({
  step,
  stepX,
  centreY,
  overrideY,
  isSelected,
  activePollIndex,
  liveCustomisationData,
  campaignFeatures,
  parentStepX,
  parentStepY,
  nextStepX,
  nextStepY,
  nextStepHalfW,
  onHeightChange,
  onQuestionChange,
  onAnswerChange,
  onAddAnswer,
  onRemoveAnswer,
  onRemovePoll,
  onPollClick,
  onAddPollClick,
  audioSiblingRightX,
  audioSiblingMidY,
}) {
  const stepY = overrideY ?? ny(step, centreY);

  // Resolve polls
  const tabKey  = step.customisationConfig?.tabKey;
  const varKey  = step.customisationConfig?.varKey;
  const varName = step.customisationConfig?.variables?.[0]?.variableName;
  const liveArr = liveCustomisationData && tabKey && varKey && varName
    ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName]
    : null;
  const polls = Array.isArray(liveArr) ? liveArr
    : (step.populatedData?.variables?.[0]?.allValues || []);

  const getPollAnswers = (poll) => {
    if (Array.isArray(poll?.answers) && poll.answers.length > 0) {
      return poll.answers
        .map(ans => typeof ans === 'string' ? ans : (ans?.text || ans?.label || ''))
        .filter(ans => ans !== null && ans !== undefined && String(ans).trim() !== '')
        .slice(0, MAX_POLL_ANSWERS);
    }
    const answerKeys = Object.keys(poll || {})
      .filter(k => /^answer\d+$/.test(k))
      .sort((a, b) => Number(a.replace('answer', '')) - Number(b.replace('answer', '')));
    return answerKeys
      .map(k => poll?.[k] ?? '')
      .filter(ans => ans !== null && ans !== undefined && String(ans).trim() !== '')
      .slice(0, MAX_POLL_ANSWERS);
  };

  // Per-card expanded state
  const [expanded, setExpanded] = useState({});
  const isExpanded = (i) => !!expanded[i];
  const toggleExpanded = (i, e) => {
    e.stopPropagation();
    setExpanded(prev => ({ ...prev, [i]: !prev[i] }));
  };

  // Compute per-card height dynamically
  const cardHeight = (poll, i) => {
    if (!isExpanded(i)) return COLLAPSED_H;
    const answers = getPollAnswers(poll);
    return COLLAPSED_H
      + EDITOR_SECTION_GAP
      + EDITOR_QUESTION_H
      + EDITOR_SECTION_GAP
      + answers.length * EDITOR_ANSWER_ROW_H
      + EDITOR_ADD_BTN_H
      + EDITOR_BOTTOM_PAD
      + EDITOR_CONTENT_BUFFER;
  };

  const rowHeight = (poll, i) => cardHeight(poll, i);

  // Total stack height
  const totalH = polls.length === 0
    ? COLLAPSED_H
    : polls.reduce((sum, poll, i) => sum + rowHeight(poll, i), 0)
        + (polls.length - 1) * POLL_CARD_GAP;

  useEffect(() => {
    onHeightChange?.(totalH);
    return () => onHeightChange?.(null);
  }, [onHeightChange, totalH]);

  const x  = stepX - CARD_W / 2;
  const y  = stepY - totalH / 2;
  const targetLeftX = nextStepX != null ? nextStepX - (nextStepHalfW ?? 58) : null;
  // Space between the left branch spine and the poll card left edge
  const LEFT_BRANCH_GAP = 12;
  // Space between the rightmost trigger panel and the exit collection stem
  const RIGHT_EXIT_MARGIN = 18;

  // Pre-compute per-row geometry (used by both left and right connectors)
  const rowPositions = polls.map((poll, i) => {
    let accY = y;
    for (let j = 0; j < i; j++) {
      accY += rowHeight(polls[j], j) + POLL_CARD_GAP;
    }
    const rH = rowHeight(poll, i);
    return {
      rowY:     accY,
      rowH:     rH,
      rowMidY:  accY + rH / 2,
      rowExitX: x + CARD_W,
    };
  });

  // ── Left branching connector from parent (Poll Data circle) ─────────────────
  // Poll Data right edge → + → spine → spurs into each poll
  const connectorGroup = (() => {
    if (parentStepX == null || parentStepY == null) return null;
    const fromX     = parentStepX + CIRCLE_R;  // Poll Data right edge
    const fromY     = parentStepY;
    const spineX    = x - LEFT_BRANCH_GAP;
    const topY      = rowPositions.length > 0 ? rowPositions[0].rowMidY                       : stepY;
    const botY      = rowPositions.length > 0 ? rowPositions[rowPositions.length - 1].rowMidY : stepY;
    const spineTopY = Math.min(topY, fromY);
    // + sits midway between Poll Data right edge and spine
    const addX = fromX + (spineX - fromX) / 2;
    const addY = fromY;
    return (
      <g>
        {/* Poll Data right edge → + */}
        <path d={`M ${fromX} ${fromY} H ${addX - PLUS_R}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
        {/* + button */}
        <circle cx={addX} cy={addY} r={PLUS_R} fill={C.greenText} style={{ cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); onAddPollClick?.(); }} />
        <text x={addX} y={addY + 4.5} fontSize="13" fontWeight="700" fill="#fff"
          textAnchor="middle" style={{ userSelect: 'none', pointerEvents: 'none' }}>+</text>
        {/* + → spine */}
        <path d={`M ${addX + PLUS_R} ${fromY} H ${spineX}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
        {/* Vertical spine */}
        <path d={`M ${spineX} ${spineTopY} V ${botY}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
        {/* Horizontal spurs into each poll card */}
        {rowPositions.map((rp, ri) => (
          <path key={`left-spur-${ri}`} d={`M ${spineX} ${rp.rowMidY} H ${x}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
        ))}
      </g>
    );
  })();

  // ── Render individual poll cards ──────────────────────────────────────────
  let curY = y;
  const cardElements = polls.length === 0 ? (
    // Empty state — single placeholder card
    <g>
      <rect x={x} y={y} width={CARD_W} height={COLLAPSED_H}
        rx={BORDER_R} ry={BORDER_R}
        fill={C.card} stroke={isSelected ? C.purple : C.border} strokeWidth={1.5} />
      <text x={stepX} y={y + COLLAPSED_H / 2 + 4}
        fontSize="10.5" fill={C.textDim} textAnchor="middle"
        style={{ userSelect: 'none' }}>No polls configured</text>
    </g>
  ) : polls.map((poll, i) => {
    const pollTitle  = poll?.title || `Poll ${i + 1}`;
    const pollType   = (poll?.type || 'opinion').toLowerCase();
    const pollQ      = poll?.question || '';
    const answers    = getPollAnswers(poll);
    const badgeColor = TYPE_COLORS[pollType] || TYPE_COLORS.opinion;
    const typeLabel  = pollType.charAt(0).toUpperCase() + pollType.slice(1);
    const h          = cardHeight(poll, i);
    const rowH       = rowHeight(poll, i);
    const rowY       = curY;
    curY += rowH + POLL_CARD_GAP;
    const cy         = rowY + (rowH - h) / 2;
    const exp        = isExpanded(i);
    const isActivePoll = activePollIndex === i;
    const borderCol  = isActivePoll ? C.greenText : (isSelected ? C.sel : C.border);
    const borderW    = isActivePoll ? 2 : 1.5;

    // Chevron points: collapsed = ▼, expanded = ▲
    const chevX = x + CARD_W - 28;
    const chevY = cy + COLLAPSED_H / 2;
    const chevron = exp
      ? `M ${chevX - 5} ${chevY + 3} L ${chevX} ${chevY - 3} L ${chevX + 5} ${chevY + 3}`
      : `M ${chevX - 5} ${chevY - 3} L ${chevX} ${chevY + 3} L ${chevX + 5} ${chevY - 3}`;
    const deleteCx = x + CARD_W - 14;
    const deleteCy = cy + 14;

    return (
      <g key={i}>
        {/* Card background */}
        <rect x={x} y={cy} width={CARD_W} height={h}
          rx={BORDER_R} ry={BORDER_R}
          fill={C.card} stroke={borderCol} strokeWidth={borderW}
          style={{ cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); onPollClick?.(i); }}
        />

        {/* ── Collapsed header row ── */}
        {/* Type badge */}
        <rect x={x + CARD_PAD} y={cy + (COLLAPSED_H - BADGE_H) / 2}
          width={BADGE_W} height={BADGE_H} rx={4} ry={4}
          fill={`${badgeColor}22`} stroke={badgeColor} strokeWidth={1} />
        <text x={x + CARD_PAD + BADGE_W / 2} y={cy + (COLLAPSED_H - BADGE_H) / 2 + BADGE_H / 2 + 4}
          fontSize="9" fontWeight="700" fill={badgeColor} textAnchor="middle"
          style={{ userSelect: 'none' }}>{typeLabel}</text>

        {/* Poll title */}
        <text x={x + CARD_PAD + BADGE_W + 8} y={cy + COLLAPSED_H / 2 + 4}
          fontSize="10.5" fontWeight="600" fill={C.text}
          style={{ userSelect: 'none' }}>
          {pollTitle.length > 14 ? pollTitle.slice(0, 12) + '…' : pollTitle}
        </text>

        {/* Expand/collapse chevron */}
        <path d={chevron} fill="none" stroke={C.textMuted} strokeWidth={1.5}
          strokeLinecap="round" strokeLinejoin="round"
          style={{ cursor: 'pointer' }}
          onClick={(e) => toggleExpanded(i, e)} />
        {/* Invisible larger hit target for chevron */}
        <rect x={chevX - 12} y={chevY - 12} width={24} height={24}
          fill="transparent" style={{ cursor: 'pointer' }}
          onClick={(e) => toggleExpanded(i, e)} />

        {/* Delete poll */}
        {onRemovePoll && (
          <g
            style={{ cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); onRemovePoll(i); }}
          >
            <circle cx={deleteCx} cy={deleteCy} r={10} fill="#dc2626" />
            <g transform={`translate(${deleteCx}, ${deleteCy})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
              <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
              <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
              <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
              <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
              <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
            </g>
          </g>
        )}

        {/* ── Expanded detail ── */}
        {exp && (
          <g>
            {/* Divider below header */}
            <line x1={x + 8} y1={cy + COLLAPSED_H}
              x2={x + CARD_W - 8} y2={cy + COLLAPSED_H}
              stroke={C.border} strokeWidth={1} />
            <foreignObject
              x={x + CARD_PAD}
              y={cy + COLLAPSED_H + 8}
              width={CARD_W - CARD_PAD * 2}
              height={h - COLLAPSED_H - EDITOR_BOTTOM_PAD}
            >
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style={{ display: 'flex', flexDirection: 'column', gap: `${EDITOR_SECTION_GAP}px`, width: '100%', height: '100%', boxSizing: 'border-box' }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ fontSize: '9.5px', fontWeight: 600, color: C.textMuted, userSelect: 'none' }}>QUESTION</div>
                  <textarea
                    key={`q-${i}-${pollQ}`}
                    defaultValue={pollQ}
                    placeholder="Enter poll question"
                    onBlur={(e) => onQuestionChange?.(i, e.target.value)}
                    style={{
                      minHeight: '52px',
                      borderRadius: '8px',
                      border: '1px solid var(--fc-border)',
                      background: 'var(--fc-circle-bg)',
                      color: 'var(--fc-text)',
                      padding: '10px',
                      fontSize: '10px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ fontSize: '9.5px', fontWeight: 600, color: C.textMuted, userSelect: 'none' }}>ANSWERS</div>
                  {answers.map((ans, ai) => (
                    <div key={`ans-${i}-${ai}`} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: badgeColor, opacity: 0.8, flexShrink: 0 }} />
                      <input
                        key={`ans-input-${i}-${ai}-${ans}`}
                        defaultValue={ans}
                        placeholder={`Option ${ai + 1}`}
                        onBlur={(e) => onAnswerChange?.(i, ai, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        style={{
                          flex: 1,
                          height: '28px',
                          borderRadius: '8px',
                          border: '1px solid var(--fc-border)',
                          background: 'var(--fc-circle-bg)',
                          color: 'var(--fc-text)',
                          padding: '0 10px',
                          fontSize: '9.5px',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                      <button
                        type="button"
                        disabled={answers.length <= 2}
                        onClick={() => onRemoveAnswer?.(i, ai)}
                        style={{
                          width: '26px',
                          height: '26px',
                          borderRadius: '7px',
                          border: answers.length <= 2 ? `1px solid var(--fc-border)` : 'none',
                          background: answers.length <= 2 ? 'var(--fc-circle-bg)' : '#dc2626',
                          color: answers.length <= 2 ? C.textDim : '#fee2e2',
                          cursor: answers.length <= 2 ? 'not-allowed' : 'pointer',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        title={answers.length <= 2 ? 'At least 2 answers are required' : 'Remove answer'}
                      >
                        {answers.length <= 2 ? '×' : (
                          <svg width="11" height="12" viewBox="-6 -6 12 12.5" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
                            <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
                            <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
                            <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                            <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                          </svg>
                        )}
                      </button>
                    </div>
                  ))}
                  {answers.length < MAX_POLL_ANSWERS && (
                    <button
                      type="button"
                      onClick={() => onAddAnswer?.(i)}
                      style={{
                        height: `${EDITOR_ADD_BTN_H}px`,
                        borderRadius: '8px',
                        border: '1px dashed rgba(145,70,255,0.35)',
                        background: 'transparent',
                        color: C.purple,
                        fontSize: '9.5px',
                        cursor: 'pointer',
                      }}
                    >
                      ＋ Add answer
                    </button>
                  )}
                </div>

              </div>
            </foreignObject>
          </g>
        )}
      </g>
    );
  });

  const outgoingConnectorGroup = (() => {
    if (polls.length === 0 || nextStepX == null || nextStepY == null || rowPositions.length === 0) return null;
    const maxRowExitX = Math.max(...rowPositions.map(rp => rp.rowExitX));
    const exitStemX   = maxRowExitX + RIGHT_EXIT_MARGIN;
    const topY        = rowPositions[0].rowMidY;
    const bottomY     = rowPositions[rowPositions.length - 1].rowMidY;
    // Extend stem up to audio sibling if present (it merges into the same stem)
    const stemTopY    = (audioSiblingMidY != null && audioSiblingMidY < topY) ? audioSiblingMidY : topY;
    // Exit horizontal should meet the next node (Vote End) at its centre/title line.
    // Using parentStepY broke after poll_data_node became the direct parent, because
    // that relay node is pushed down with the polls while Vote End stays centred.
    const exitY = nextStepY ?? parentStepY ?? (stemTopY + bottomY) / 2;
    // Ensure the vertical stem covers the exit Y point even if it falls outside the poll range
    const stemFinalTopY = Math.min(stemTopY, exitY);
    const stemFinalBotY = Math.max(bottomY, exitY);
    return (
      <g>
        {/* Audio sibling exit: horizontal from audio right edge into the stem */}
        {audioSiblingRightX != null && audioSiblingMidY != null && (
          <path
            d={`M ${audioSiblingRightX} ${audioSiblingMidY} H ${exitStemX}`}
            stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82}
          />
        )}
        {/* Each poll row runs horizontally to the exit collection stem */}
        {rowPositions.map((rp, idx) => (
          <path
            key={`poll-exit-row-${idx}`}
            d={`M ${rp.rowExitX} ${rp.rowMidY} H ${exitStemX}`}
            stroke={C.purple}
            strokeWidth={1.5}
            fill="none"
            strokeOpacity={0.82}
          />
        ))}
        {/* Vertical collection stem spanning audio top to last poll (extended to cover exitY) */}
        <path
          d={`M ${exitStemX} ${stemFinalTopY} V ${stemFinalBotY}`}
          stroke={C.purple}
          strokeWidth={1.5}
          fill="none"
          strokeOpacity={0.82}
        />
        {/* Single exit aligned to the next node centre so it connects cleanly */}
        <path
          d={`M ${exitStemX} ${exitY} H ${targetLeftX}`}
          stroke={C.purple}
          strokeWidth={1.5}
          fill="none"
          strokeOpacity={0.82}
        />
      </g>
    );
  })();

  return (
    <g className="fc-node">
      {connectorGroup}
      {outgoingConnectorGroup}
      {cardElements}
    </g>
  );
}
